import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPageScroll } from '@/lib/resetPageScroll';
import { GooglePlacesAutocomplete } from '@/components/ui/GooglePlacesAutocomplete';
import { EnglishDateTimeInput } from '@/components/ui/EnglishDateInput';
import { CurrencyAmountInput } from '@/components/ui/CurrencyAmountInput';
import { InlineFieldError } from '@/components/ui/FormFeedback';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import {
  postCreateOrder,
  timeSlotToServiceTime,
  toAvailableServicerServiceTime,
  formatOrderDurationHours,
} from '@/api/modules/order';
import {
  ApiRequestError,
  fetchAdminTeamMembers,
  fetchInvoiceSettings,
  fetchSystemUserInfo,
  readTeamCountry,
  showApiResultModal,
  type TeamMember,
} from '@/api';
import {
  DEFAULT_TEAM_COUNTRY,
  normalizeTeamCountry,
  teamCountryCurrency,
  teamCountryDialCode,
  teamCountryToPlacesCode,
  type TeamCountryCode,
} from '@/constants/teamCountries';
import {
  EMPTY_PARSED_PLACE_ADDRESS,
  hasCompleteParsedAddress,
  type ParsedPlaceAddress,
} from '@/lib/parseGooglePlaceAddress';
import { geocodeServiceAddress } from '@/lib/geocodeServiceAddress';
import {
  hasRecognizedContact,
  parseOrderContactText,
} from '@/lib/parseOrderContactText';
import {
  exGstToIncGst,
  formatMoneyInput,
  incGstToExGst,
  parseMoneyInput,
} from '@/lib/orderPricing';
import { validateTeamPhone } from '@/lib/teamPhone';

/** Invoice settings store tax rate as a percent (10 = 10%). 0% is a valid rate. */
function parseInvoiceTaxPercent(raw: string | undefined): number | null {
  if (raw == null || raw.trim() === '') return null;
  const n = Number(raw.trim().replace(/%/g, ''));
  if (!Number.isFinite(n) || n < 0 || n > 100) return null;
  return Math.round(n * 1000) / 1000;
}

function invoicePercentToDecimal(percent: number): number {
  return Math.round((percent / 100) * 10000) / 10000;
}

function formatInvoiceTaxPercent(percent: number): string {
  const rounded = Math.round(percent * 1000) / 1000;
  return Number.isInteger(rounded) ? String(rounded) : String(rounded);
}

/** Show the registration dial code once. Numbers that already start with + are left as typed. */
function phoneWithDialCode(dialCode: string, raw: string): string {
  const value = raw.trim();
  if (!value) return '';
  if (value.startsWith('+') || value.replace(/\s/g, '').startsWith(dialCode)) return value;
  return `${dialCode} ${value}`;
}

const DURATION_MIN = 0.5;
const DURATION_MAX = 24;
const DURATION_STEP = 0.5;
const DEFAULT_MANUAL_DURATION = 1;

function clampOrderDuration(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_MANUAL_DURATION;
  const stepped = Math.round(value / DURATION_STEP) * DURATION_STEP;
  return Math.min(DURATION_MAX, Math.max(DURATION_MIN, stepped));
}

interface FormData {
  customerName: string;
  customerPhone: string;
  customerEmail: string;
  customerAddress: string;
  addressDetails: ParsedPlaceAddress;
  date: string;
  timeSlot: string;
  priceExGst: string;
  priceIncGst: string;
  manualDuration: number;
  notes: string;
}

const defaultForm: FormData = {
  customerName: '',
  customerPhone: '',
  customerEmail: '',
  customerAddress: '',
  addressDetails: { ...EMPTY_PARSED_PLACE_ADDRESS },
  date: '',
  timeSlot: '',
  priceExGst: '',
  priceIncGst: '',
  manualDuration: DEFAULT_MANUAL_DURATION,
  notes: '',
};

const SECTION_FIELDS: (keyof FormData)[][] = [
  ['customerName', 'customerPhone', 'customerEmail', 'customerAddress'],
  ['date', 'timeSlot', 'manualDuration', 'priceExGst', 'priceIncGst'],
];

const inputBaseClass =
  'w-full h-8 px-2.5 text-xs font-normal rounded-md border outline-none transition disabled:bg-gray-50 disabled:text-gray-400 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400';

function inputClass(invalid: boolean): string {
  return `${inputBaseClass} ${
    invalid
      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
      : 'border-gray-200 bg-white focus:border-green-500'
  }`;
}

interface FormSectionProps {
  index: number;
  title: string;
  hint?: string;
  hasError?: boolean;
  sectionRef?: (el: HTMLDivElement | null) => void;
  headerAction?: React.ReactNode;
  children: React.ReactNode;
}

function FormSection({
  index,
  title,
  hint,
  hasError = false,
  sectionRef,
  headerAction,
  children,
}: FormSectionProps) {
  return (
    <div
      ref={sectionRef}
      className={`bg-white rounded-lg border ${hasError ? 'border-red-300' : 'border-gray-200'}`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <span
            className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-semibold flex-shrink-0 ${
              hasError ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
            }`}
          >
            {index + 1}
          </span>
          <span className={`text-xs font-semibold ${hasError ? 'text-red-600' : 'text-gray-800'}`}>
            {title}
          </span>
          {hint ? <span className="text-gray-400 text-[10px] font-normal">{hint}</span> : null}
        </div>
        {headerAction ? <div className="flex-shrink-0">{headerAction}</div> : null}
      </div>
      <div className="px-3 pb-3">{children}</div>
    </div>
  );
}

function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-[10px] font-normal text-gray-400 mb-1">{label}</div>
      <div className="h-8 px-2.5 rounded-md border border-gray-100 bg-gray-50 text-xs text-gray-600 flex items-center truncate">
        {value || '—'}
      </div>
    </div>
  );
}

function FieldLabel({
  text,
  required,
  hint,
}: {
  text: string;
  required?: boolean;
  hint?: string;
}) {
  return (
    <label className="block text-xs font-normal text-gray-700 mb-1">
      {text}
      {required ? <span className="text-red-400"> *</span> : null}
      {hint ? <span className="text-gray-400 font-normal"> {hint}</span> : null}
    </label>
  );
}

export default function CreateOrderPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const backTo = (location.state as { from?: string } | null)?.from === '/team' ? '/team' : '/orders';
  const [form, setForm] = useState<FormData>(defaultForm);
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});
  const [submitting, setSubmitting] = useState(false);
  const [showRecognizeModal, setShowRecognizeModal] = useState(false);
  const [recognizeText, setRecognizeText] = useState('');
  const [recognizeLoading, setRecognizeLoading] = useState(false);
  const [recognizeError, setRecognizeError] = useState('');
  const [taxName, setTaxName] = useState('GST');
  const [taxPercent, setTaxPercent] = useState<number | null>(null);
  const [taxLoading, setTaxLoading] = useState(true);
  const gstRate = taxPercent === null ? null : invoicePercentToDecimal(taxPercent);
  const [teamCountry, setTeamCountry] = useState<TeamCountryCode>(DEFAULT_TEAM_COUNTRY);
  const [registrationCountry, setRegistrationCountry] = useState<TeamCountryCode>(DEFAULT_TEAM_COUNTRY);
  const [teamCountryReady, setTeamCountryReady] = useState(false);
  const [durationError, setDurationError] = useState('');
  const [staffMembers, setStaffMembers] = useState<TeamMember[]>([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffError, setStaffError] = useState('');
  const [selectedStaffIds, setSelectedStaffIds] = useState<string[]>([]);
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => () => {
    resetPageScroll();
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setTaxLoading(true);
      try {
        const profile = await fetchSystemUserInfo();
        if (cancelled) return;
        const fromInfo = readTeamCountry(profile.team_info);
        if (fromInfo) {
          const country = normalizeTeamCountry(fromInfo);
          setTeamCountry(country);
          setRegistrationCountry(country);
        }
      } catch {
        /* country stays on the default until invoice settings load */
      }

      try {
        const settings = await fetchInvoiceSettings();
        if (cancelled) return;
        if (settings.country) setTeamCountry(normalizeTeamCountry(settings.country));
        const name = settings.values.tax_name?.trim();
        if (name) setTaxName(name);
        setTaxPercent(parseInvoiceTaxPercent(settings.values.tax_rate));
      } catch {
        if (!cancelled) setTaxPercent(null);
      } finally {
        if (!cancelled) {
          setTaxLoading(false);
          setTeamCountryReady(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (gstRate === null) return;
    setForm((prev) => {
      const ex = parseMoneyInput(prev.priceExGst);
      if (ex === null) return prev;
      return { ...prev, priceIncGst: formatMoneyInput(exGstToIncGst(ex, gstRate)) };
    });
  }, [gstRate]);

  const todayStr = new Date().toISOString().split('T')[0];
  const { addressDetails } = form;

  const serviceDuration = useMemo(
    () => clampOrderDuration(form.manualDuration),
    [form.manualDuration],
  );

  const parsedPriceExGst = useMemo(() => parseMoneyInput(form.priceExGst), [form.priceExGst]);
  const parsedPriceIncGst = useMemo(() => parseMoneyInput(form.priceIncGst), [form.priceIncGst]);
  const currency = teamCountryCurrency(teamCountry);
  const dialCode = teamCountryDialCode(registrationCountry);

  useEffect(() => {
    let cancelled = false;
    setStaffLoading(true);
    setStaffError('');

    const serviceTime =
      form.date && form.timeSlot
        ? toAvailableServicerServiceTime(timeSlotToServiceTime(form.date, form.timeSlot))
        : undefined;

    fetchAdminTeamMembers({
      city: form.addressDetails.city || undefined,
      postalCode: form.addressDetails.postal_code || undefined,
      serviceTime,
    })
      .then((list) => {
        if (cancelled) return;
        setStaffMembers(list);
        setSelectedStaffIds((prev) => prev.filter((id) => list.some((member) => member.id === id)));
      })
      .catch((e) => {
        if (cancelled) return;
        setStaffMembers([]);
        setStaffError(e instanceof ApiRequestError ? e.message : 'Failed to load staff.');
      })
      .finally(() => {
        if (!cancelled) setStaffLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [form.date, form.timeSlot, form.addressDetails.city, form.addressDetails.postal_code]);

  const toggleStaff = useCallback((id: string) => {
    setSelectedStaffIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  }, []);

  const selectedStaffLabel = useMemo(() => {
    if (selectedStaffIds.length === 0) return 'Unassigned';
    const names = staffMembers
      .filter((member) => selectedStaffIds.includes(member.id))
      .map((member) => member.name);
    return names.length > 0 ? names.join(', ') : `${selectedStaffIds.length} selected`;
  }, [selectedStaffIds, staffMembers]);

  const update = useCallback(
    (key: keyof FormData, value: string | ParsedPlaceAddress | number) => {
      setForm((prev) => ({ ...prev, [key]: value }));
      setErrors((prev) => ({ ...prev, [key]: '' }));
      if (key === 'manualDuration') setDurationError('');
    },
    [],
  );

  const updatePriceExGst = useCallback((value: string) => {
    setForm((prev) => {
      const next = { ...prev, priceExGst: value };
      const parsed = parseMoneyInput(value);
      if (parsed !== null && gstRate !== null) {
        next.priceIncGst = formatMoneyInput(exGstToIncGst(parsed, gstRate));
      } else if (!value.trim()) {
        next.priceIncGst = '';
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, priceExGst: '', priceIncGst: '' }));
  }, [gstRate]);

  const updatePriceIncGst = useCallback((value: string) => {
    setForm((prev) => {
      const next = { ...prev, priceIncGst: value };
      const parsed = parseMoneyInput(value);
      if (parsed !== null && gstRate !== null) {
        next.priceExGst = formatMoneyInput(incGstToExGst(parsed, gstRate));
      } else if (!value.trim()) {
        next.priceExGst = '';
      }
      return next;
    });
    setErrors((prev) => ({ ...prev, priceExGst: '', priceIncGst: '' }));
  }, [gstRate]);

  const handleAddressChange = useCallback(
    (address: string) => {
      setForm((prev) => ({
        ...prev,
        customerAddress: address,
        addressDetails: { ...EMPTY_PARSED_PLACE_ADDRESS, formattedAddress: address },
      }));
      setErrors((prev) => ({ ...prev, customerAddress: '' }));
    },
    [],
  );

  const handlePlaceSelect = useCallback((details: ParsedPlaceAddress) => {
    setForm((prev) => ({
      ...prev,
      customerAddress: details.formattedAddress,
      addressDetails: details,
    }));
    setErrors((prev) => ({ ...prev, customerAddress: '' }));
  }, []);

  const openRecognizeModal = useCallback(() => {
    setRecognizeError('');
    setShowRecognizeModal(true);
  }, []);

  const closeRecognizeModal = useCallback(() => {
    if (recognizeLoading) return;
    setShowRecognizeModal(false);
    setRecognizeError('');
  }, [recognizeLoading]);

  const handleRecognizeText = useCallback(async () => {
    const parsed = parseOrderContactText(recognizeText);
    if (!hasRecognizedContact(parsed)) {
      setRecognizeError('Could not recognize any customer information. Please check the text format.');
      return;
    }

    setRecognizeLoading(true);
    setRecognizeError('');

    try {
      let addressDetails = { ...EMPTY_PARSED_PLACE_ADDRESS };
      let customerAddress = '';

      if (parsed.serviceAddress) {
        addressDetails = await geocodeServiceAddress(parsed.serviceAddress, teamCountry);
        customerAddress = addressDetails.formattedAddress || parsed.serviceAddress;
        if (!hasCompleteParsedAddress(addressDetails)) {
          throw new Error('Address recognized but Google Maps could not parse all required fields.');
        }
      }

      setForm((prev) => ({
        ...prev,
        customerName: parsed.customerName || prev.customerName,
        customerPhone: parsed.customerPhone || prev.customerPhone,
        customerEmail: parsed.customerEmail || prev.customerEmail,
        customerAddress: customerAddress || prev.customerAddress,
        addressDetails: parsed.serviceAddress ? addressDetails : prev.addressDetails,
      }));
      setErrors((prev) => ({
        ...prev,
        customerName: '',
        customerPhone: '',
        customerEmail: '',
        customerAddress: '',
      }));
      setShowRecognizeModal(false);
      setRecognizeText('');
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to recognize address.';
      if (parsed.customerName || parsed.customerPhone || parsed.customerEmail) {
        setForm((prev) => ({
          ...prev,
          customerName: parsed.customerName || prev.customerName,
          customerPhone: parsed.customerPhone || prev.customerPhone,
          customerEmail: parsed.customerEmail || prev.customerEmail,
        }));
        setRecognizeError(`${message} Other fields have been filled in.`);
      } else {
        setRecognizeError(message);
      }
    } finally {
      setRecognizeLoading(false);
    }
  }, [recognizeText, teamCountry]);

  const validate = useCallback((): boolean => {
    const errs: Partial<Record<keyof FormData, string>> = {};
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    if (!form.customerName.trim()) errs.customerName = 'Customer name is required';
    if (!form.customerAddress.trim()) errs.customerAddress = 'Address is required';
    if (!hasCompleteParsedAddress(form.addressDetails)) {
      errs.customerAddress = 'Please select a valid address from suggestions';
    }
    if (!form.customerEmail.trim()) {
      errs.customerEmail = 'Email is required to create an order';
    } else if (!emailPattern.test(form.customerEmail.trim())) {
      errs.customerEmail = 'Please enter a valid email address';
    }
    const phoneError = validateTeamPhone(form.customerPhone, registrationCountry, false);
    if (phoneError) errs.customerPhone = phoneError;
    if (!form.date || !form.timeSlot) errs.date = 'Please select date and time';
    if (gstRate === null) {
      errs.priceExGst = `Please set the ${taxName} rate in Invoice Settings first.`;
    } else {
      const ex = parseMoneyInput(form.priceExGst);
      const inc = parseMoneyInput(form.priceIncGst);
      if (!ex || ex <= 0) errs.priceExGst = `Ex-${taxName} total is required.`;
      if (!inc || inc <= 0) errs.priceIncGst = `Inc-${taxName} total is required.`;
    }

    let nextDurationError = '';
    if (form.manualDuration < DURATION_MIN) {
      nextDurationError = `Work duration must be at least ${DURATION_MIN} hours.`;
    }
    setDurationError(nextDurationError);

    setErrors(errs);

    const firstErrorSection = SECTION_FIELDS.findIndex((fields) =>
      fields.some((f) => errs[f]),
    );
    if (firstErrorSection !== -1) {
      setTimeout(() => {
        sectionRefs.current[firstErrorSection]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    } else if (nextDurationError) {
      setTimeout(() => {
        sectionRefs.current[1]?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }

    return Object.keys(errs).length === 0 && !nextDurationError;
  }, [form, gstRate, registrationCountry, taxName]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) return;
    if (gstRate === null || parsedPriceExGst === null || parsedPriceIncGst === null) return;

    setSubmitting(true);

    try {
      const result = await postCreateOrder({
        custName: form.customerName,
        email: form.customerEmail,
        phone: phoneWithDialCode(dialCode, form.customerPhone),
        address: form.customerAddress,
        state: form.addressDetails.state,
        street: form.addressDetails.street,
        streetNumber: form.addressDetails.street_number,
        postalCode: form.addressDetails.postal_code,
        city: form.addressDetails.city,
        serviceTime: timeSlotToServiceTime(form.date, form.timeSlot),
        otherInfo: form.notes,
        totalPrice: parsedPriceExGst,
        totalPriceWithGst: parsedPriceIncGst,
        gstRateDecimal: gstRate,
        serviceDuration,
        orderItems: [],
        assigneeIds: selectedStaffIds,
      });
      showApiResultModal({
        message: result.message,
        success: true,
        onDismiss: () => {
          resetPageScroll();
          navigate('/team', { replace: true, state: { focusDate: form.date } });
        },
      });
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to create order.',
        success: false,
      });
    } finally {
      setSubmitting(false);
    }
  }, [
    validate,
    navigate,
    form,
    dialCode,
    gstRate,
    parsedPriceExGst,
    parsedPriceIncGst,
    serviceDuration,
    selectedStaffIds,
  ]);

  const setSectionRef = useCallback(
    (index: number) => (el: HTMLDivElement | null) => {
      sectionRefs.current[index] = el;
    },
    [],
  );

  return (
    <div className="min-h-screen bg-[#f8f9fa]">
      <SubpageHeader
        title="New Order"
        subtitle="Fill in all required fields"
        onBack={() => navigate(backTo)}
      />

      {showRecognizeModal && (
        <>
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={closeRecognizeModal}
          />
          <div className="fixed inset-x-0 bottom-0 z-50 bg-white rounded-t-2xl border-t border-gray-200 px-4 pt-4 pb-8 max-h-[85vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="text-gray-800 font-semibold text-sm">Auto Recognize</div>
                <div className="text-gray-400 text-xs mt-0.5">
                  Paste customer details and we will fill the form automatically
                </div>
              </div>
              <button
                type="button"
                onClick={closeRecognizeModal}
                disabled={recognizeLoading}
                className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center cursor-pointer disabled:opacity-50"
              >
                <i className="ri-close-line text-gray-500" />
              </button>
            </div>

            <textarea
              value={recognizeText}
              onChange={(e) => {
                setRecognizeText(e.target.value);
                setRecognizeError('');
              }}
              placeholder={`Example:\nFull Name: John Smith\nPhone: 0412 345 678\nEmail: john@example.com\nService Address: 12 George St, Sydney NSW 2000`}
              rows={8}
              disabled={recognizeLoading}
              className="w-full px-2.5 py-1.5 rounded-md border border-gray-200 focus:border-green-500 text-xs text-gray-800 outline-none bg-white placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 resize-none disabled:opacity-60"
            />

            {recognizeError && (
              <div className="text-red-500 text-xs mt-2">{recognizeError}</div>
            )}

            <div className="flex gap-2 mt-4">
              <button
                type="button"
                onClick={closeRecognizeModal}
                disabled={recognizeLoading}
                className="flex-1 h-9 rounded-lg border border-gray-200 text-gray-600 text-xs font-medium cursor-pointer hover:bg-gray-50 disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleRecognizeText()}
                disabled={recognizeLoading || !recognizeText.trim()}
                className="flex-1 h-9 rounded-lg bg-green-500 hover:bg-green-600 text-white text-xs font-medium cursor-pointer disabled:opacity-60 flex items-center justify-center gap-1.5"
              >
                {recognizeLoading ? (
                  <>
                    <i className="ri-loader-4-line animate-spin" />
                    Recognizing...
                  </>
                ) : (
                  <>
                    <i className="ri-scan-line" />
                    Recognize
                  </>
                )}
              </button>
            </div>
          </div>
        </>
      )}

      <div className="px-4 pt-3 space-y-2.5 pb-[calc(5.25rem+env(safe-area-inset-bottom))]">
        <FormSection
          index={0}
          title="Customer Information"
          hasError={SECTION_FIELDS[0].some((f) => errors[f])}
          sectionRef={setSectionRef(0)}
          headerAction={
            <button
              type="button"
              onClick={openRecognizeModal}
              className="h-7 px-2.5 rounded-md bg-green-50 border border-green-200 text-green-700 text-xs font-medium cursor-pointer flex items-center gap-1"
            >
              <i className="ri-magic-line text-xs" />
              Smart Fill
            </button>
          }
        >
          <div className="space-y-2.5">
            <div>
              <FieldLabel text="Full Name" required />
              <input
                id="order-customerName"
                value={form.customerName}
                onChange={(e) => update('customerName', e.target.value)}
                placeholder="Enter customer full name"
                aria-invalid={!!errors.customerName}
                aria-describedby={errors.customerName ? 'order-customerName-error' : undefined}
                className={inputClass(!!errors.customerName)}
              />
              <InlineFieldError id="order-customerName-error" message={errors.customerName} />
            </div>

            <div>
              <FieldLabel text="Phone Number" hint="(optional)" />
              <div
                className={`flex h-8 w-full rounded-md border overflow-hidden ${
                  errors.customerPhone
                    ? 'border-red-300 bg-red-50/35 focus-within:border-red-400'
                    : 'border-gray-200 bg-white focus-within:border-green-500'
                }`}
              >
                <span className="inline-flex items-center px-2.5 bg-gray-50 border-r border-gray-200 text-xs font-medium text-gray-500 flex-shrink-0 select-none">
                  {dialCode}
                </span>
                <input
                  id="order-customerPhone"
                  value={form.customerPhone}
                  onChange={(e) => update('customerPhone', e.target.value)}
                  placeholder="Enter phone number"
                  type="tel"
                  inputMode="tel"
                  aria-invalid={!!errors.customerPhone}
                  aria-describedby={errors.customerPhone ? 'order-customerPhone-error' : undefined}
                  className="flex-1 min-w-0 h-full px-2.5 text-xs font-normal text-gray-800 outline-none border-0 bg-transparent placeholder:font-normal placeholder:text-xs placeholder:text-gray-400"
                />
              </div>
              <InlineFieldError id="order-customerPhone-error" message={errors.customerPhone} />
            </div>

            <div>
              <FieldLabel text="Email" required />
              <input
                id="order-customerEmail"
                value={form.customerEmail}
                onChange={(e) => update('customerEmail', e.target.value)}
                placeholder="Enter email address"
                type="email"
                autoComplete="email"
                aria-invalid={!!errors.customerEmail}
                aria-describedby={errors.customerEmail ? 'order-customerEmail-error' : undefined}
                className={inputClass(!!errors.customerEmail)}
              />
              <InlineFieldError id="order-customerEmail-error" message={errors.customerEmail} />
            </div>

            <div>
              <FieldLabel text="Service Address" required />
              <div
                className={`w-full h-8 px-2.5 rounded-md border bg-white flex items-center ${
                  errors.customerAddress
                    ? 'border-red-300 bg-red-50/35 focus-within:border-red-400'
                    : 'border-gray-200 focus-within:border-green-500'
                }`}
              >
                <GooglePlacesAutocomplete
                  id="order-customerAddress"
                  key={teamCountry}
                  value={form.customerAddress}
                  onChange={handleAddressChange}
                  onPlaceSelect={handlePlaceSelect}
                  country={teamCountryToPlacesCode(teamCountry)}
                  disabled={!teamCountryReady}
                  placeholder={
                    teamCountryReady
                      ? 'Start typing to search addresses...'
                      : 'Loading address search...'
                  }
                />
              </div>
              <InlineFieldError id="order-customerAddress-error" message={errors.customerAddress} />
            </div>

            {hasCompleteParsedAddress(addressDetails) && (
              <div className="grid grid-cols-2 gap-2 pt-0.5">
                <ReadOnlyField label="Street Number" value={addressDetails.street_number} />
                <ReadOnlyField label="Street" value={addressDetails.street} />
                <ReadOnlyField label="City" value={addressDetails.city} />
                <ReadOnlyField label="State" value={addressDetails.state} />
                <ReadOnlyField label="Postal Code" value={addressDetails.postal_code} />
              </div>
            )}
          </div>
        </FormSection>

        <FormSection
          index={1}
          title="Service Information"
          hasError={SECTION_FIELDS[1].some((f) => errors[f]) || !!durationError}
          sectionRef={setSectionRef(1)}
        >
          <div className="space-y-2.5">
            <div>
              <FieldLabel text="Work Duration" required />
              <div className="flex items-center gap-2">
                <input
                  type="range"
                  min={DURATION_MIN}
                  max={DURATION_MAX}
                  step={DURATION_STEP}
                  value={form.manualDuration}
                  onChange={(e) => update('manualDuration', clampOrderDuration(Number(e.target.value)))}
                  className="flex-1 h-2 accent-green-600 cursor-pointer"
                  aria-label="Work duration in hours"
                />
                <input
                  type="number"
                  min={DURATION_MIN}
                  max={DURATION_MAX}
                  step={DURATION_STEP}
                  value={form.manualDuration}
                  onChange={(e) => {
                    const parsed = Number(e.target.value);
                    if (!Number.isFinite(parsed)) return;
                    update('manualDuration', clampOrderDuration(parsed));
                  }}
                  className="w-14 h-8 rounded-md border border-gray-200 text-center text-xs font-normal text-gray-800 bg-white outline-none focus:border-green-500"
                  aria-label="Work duration hours"
                />
              </div>
              <InlineFieldError id="order-duration-error" message={durationError} />
            </div>

            <div>
              <FieldLabel text="Appointment" required />
              <EnglishDateTimeInput
                id="order-appointment"
                date={form.date}
                time={form.timeSlot}
                min={todayStr}
                pickerTitle="Appointment"
                placeholder="Select date and time"
                onChange={(nextDate, nextTime) => {
                  setForm((prev) => ({ ...prev, date: nextDate, timeSlot: nextTime }));
                  setErrors((prev) => ({ ...prev, date: '', timeSlot: '' }));
                }}
                aria-invalid={!!errors.date}
                aria-describedby={errors.date ? 'order-date-error' : undefined}
                className={
                  errors.date
                    ? 'border-red-300 bg-red-50/35 focus:border-red-400'
                    : 'border-gray-200 focus:border-green-500'
                }
              />
              <InlineFieldError id="order-date-error" message={errors.date} />
            </div>

            <div className="pt-2 border-t border-gray-100">
              <div className="flex items-center justify-between gap-2 mb-1.5">
                <span className="text-xs font-normal text-gray-700">
                  Order Total <span className="text-red-400">*</span>
                </span>
                {taxLoading ? (
                  <span className="text-[10px] font-medium text-gray-400 bg-gray-50 border border-gray-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    Loading tax…
                  </span>
                ) : taxPercent !== null ? (
                  <span className="text-[10px] font-medium text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    {taxName} {formatInvoiceTaxPercent(taxPercent)}%
                  </span>
                ) : (
                  <span className="text-[10px] font-medium text-amber-700 bg-amber-50 border border-amber-100 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                    {taxName} not set
                  </span>
                )}
              </div>

              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <label htmlFor="order-priceExGst" className="block text-xs font-normal text-gray-500 mb-1">
                    Ex-{taxName} ({currency.symbol}) <span className="text-red-400">*</span>
                  </label>
                  <CurrencyAmountInput
                    id="order-priceExGst"
                    type="text"
                    inputMode="decimal"
                    value={form.priceExGst}
                    onChange={updatePriceExGst}
                    currencySymbol={currency.symbol}
                    aria-invalid={!!errors.priceExGst}
                    className={
                      errors.priceExGst
                        ? '!border-red-300 !bg-red-50/35 focus-within:!border-red-400'
                        : ''
                    }
                  />
                  <InlineFieldError id="order-priceExGst-error" message={errors.priceExGst} />
                </div>

                <div>
                  <label htmlFor="order-priceIncGst" className="block text-xs font-normal text-gray-500 mb-1">
                    Inc-{taxName} ({currency.symbol}) <span className="text-red-400">*</span>
                  </label>
                  <CurrencyAmountInput
                    id="order-priceIncGst"
                    type="text"
                    inputMode="decimal"
                    value={form.priceIncGst}
                    onChange={updatePriceIncGst}
                    currencySymbol={currency.symbol}
                    aria-invalid={!!errors.priceIncGst}
                    className={
                      errors.priceIncGst
                        ? '!border-red-300 !bg-red-50/35 focus-within:!border-red-400'
                        : ''
                    }
                  />
                  <InlineFieldError id="order-priceIncGst-error" message={errors.priceIncGst} />
                </div>
              </div>
              <p className="text-[10px] text-gray-400 leading-relaxed mt-1.5">
                Enter either amount and the other will be automatically calculated based on your team&apos;s {taxName} rate.
              </p>
            </div>
          </div>
        </FormSection>

        <FormSection
          index={2}
          title="Staff Assignment"
          hint={selectedStaffIds.length > 0 ? `${selectedStaffIds.length} selected` : '(optional)'}
        >
          {staffLoading ? (
            <div className="py-4 text-center">
              <i className="ri-loader-4-line animate-spin text-green-500 text-base" />
              <p className="text-gray-400 text-xs mt-1">Loading staff…</p>
            </div>
          ) : staffError ? (
            <div className="text-xs text-red-600">{staffError}</div>
          ) : staffMembers.length === 0 ? (
            <div className="py-3 text-center text-gray-400 text-xs leading-relaxed px-1">
              {`No available staff in this area (Postal Code ${form.addressDetails.postal_code.trim() || '—'})`}
            </div>
          ) : (
            <div className="space-y-1.5 max-h-56 overflow-y-auto scrollbar-minimal">
              {staffMembers.map((member) => {
                const selected = selectedStaffIds.includes(member.id);
                const online = member.isOnline || member.status === 'working';
                const avatar =
                  member.avatar?.trim() ||
                  `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=e8f5e9&color=1b5e20`;
                return (
                  <button
                    key={member.id}
                    type="button"
                    onClick={() => toggleStaff(member.id)}
                    className={`w-full flex items-center gap-2.5 px-2 py-1.5 rounded-md border text-left cursor-pointer transition-colors ${
                      selected ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="relative flex-shrink-0 w-8 h-8">
                      <img
                        src={avatar}
                        alt=""
                        className="w-8 h-8 rounded-full object-cover object-top"
                        onError={(e) => {
                          e.currentTarget.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(member.name)}&background=e8f5e9&color=1b5e20`;
                        }}
                      />
                      <span
                        className={`absolute bottom-0 right-0 w-2 h-2 rounded-full border-2 border-white ${
                          online ? 'bg-green-400' : 'bg-gray-300'
                        }`}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-gray-800 text-xs font-medium truncate">{member.name}</div>
                      <div className={`text-[10px] ${online ? 'text-green-600' : 'text-gray-400'}`}>
                        {online ? 'Online' : 'Offline'}
                      </div>
                    </div>
                    <div
                      className={`w-4 h-4 rounded flex items-center justify-center flex-shrink-0 border ${
                        selected ? 'bg-green-500 border-green-500' : 'border-gray-300 bg-white'
                      }`}
                    >
                      {selected ? <i className="ri-check-line text-white text-[10px] leading-none" /> : null}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </FormSection>

        <FormSection index={3} title="Order Special Instructions" hint="(optional)">
          <textarea
            value={form.notes}
            onChange={(e) => update('notes', e.target.value)}
            placeholder="Special requirements, access instructions, parking info, pet notes..."
            rows={4}
            maxLength={500}
            className={`${inputBaseClass} h-auto py-1.5 border-gray-200 bg-white focus:border-green-500 resize-none leading-relaxed`}
          />
          <div className="text-right text-gray-300 text-[10px] mt-1">
            {form.notes.length}/500
          </div>
        </FormSection>

        <div className="bg-green-50 border border-green-100 rounded-lg p-3">
          <div className="text-green-700 text-xs font-semibold mb-2 flex items-center gap-1.5">
            <i className="ri-information-line text-sm" />
            Order Summary
          </div>
          {[
            { label: 'Customer', value: form.customerName || '—' },
            { label: 'Phone', value: phoneWithDialCode(dialCode, form.customerPhone) || '—' },
            { label: 'Address', value: form.customerAddress || '—' },
            {
              label: 'Schedule',
              value:
                form.date && form.timeSlot
                  ? `${form.date} ${form.timeSlot}`
                  : '—',
            },
            {
              label: 'Duration',
              value: formatOrderDurationHours(serviceDuration),
            },
            {
              label: 'Staff',
              value: selectedStaffLabel,
            },
            {
              label: `Ex-${taxName}`,
              value: parsedPriceExGst !== null ? `${currency.symbol}${formatMoneyInput(parsedPriceExGst)}` : '—',
            },
            {
              label: `Inc-${taxName}`,
              value: parsedPriceIncGst !== null ? `${currency.symbol}${formatMoneyInput(parsedPriceIncGst)}` : '—',
              highlight: true,
            },
          ].map((row) => (
            <div
              key={row.label}
              className="flex items-start gap-2 mb-1.5 last:mb-0"
            >
              <span className="text-gray-400 w-32 flex-shrink-0 text-xs font-normal pt-px">
                {row.label}
              </span>
              <span
                className={`flex-1 font-normal text-xs leading-relaxed ${
                  row.highlight ? 'text-green-600' : 'text-gray-700'
                }`}
              >
                {row.value}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 px-4 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
        <div className="mx-auto w-full" style={{ maxWidth: 480 }}>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting}
            className="ui-btn-primary"
          >
            {submitting ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base" />
                Creating...
              </>
            ) : (
              <>
                <i className="ri-check-line text-base" />
                Create Order
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}